import { useCallback, useEffect, useState } from 'react';
import { adresseApi } from '../api/client';
import { piecesApi } from '../api/resources';
import { isoVersDate } from '../api/mappers';
import { suitLesDepenses, voitLeBenefice } from '../lib/access';
import { DEVISE, fmt } from '../lib/currency';
import { champsFacture } from '../lib/facture-fournisseur';
import { COTE_MAX, reduitLImage } from '../lib/image';
import { estEnregistre } from '../lib/medias';
import { enAttente, prendEnAttente } from '../lib/pieces-attente';
import { tr, trf } from '../lib/i18n';
import { tvaFactureFournisseur } from '../lib/costs';
import { auCentime } from '../lib/vat';
import { estPhotoPourLeClient, LIBELLE_PHOTO } from '../lib/planche-photos';
import { C } from '../theme/palette';
import { ouvrePiece } from './DocumentsJoints.jsx';
import { PayeLe } from './PayeLe.jsx';
import { PhotoPleinEcran } from './PhotoPleinEcran.jsx';
import { ScanFacture } from './ScanFacture.jsx';
import { Btn } from './ui/Btn.jsx';
import { _inp } from './ui/Inputs.jsx';

const VIDE = {
  fournisseur: '',
  ref: '',
  montant: '',
  cat: '',
  articles: '',
  montantTva: null,
  date: '',
};

/**
 * Les articles d'une facture fournisseur lue, en une ligne : « Colle C2 (5 sac),
 * Carrelage 60×60 (20 m²) ». Ils accompagnent le fournisseur et le prix dans la
 * dépense de matériel, pour savoir ce qui a été acheté.
 *
 * @param {object} lu facture lue par le serveur
 * @returns {string} les articles, 300 caractères au plus
 */
export function articlesLus(lu) {
  return (Array.isArray(lu?.lignes) ? lu.lignes : [])
    .map((l) => {
      // Un tableau lu en PDF laisse de longs blancs entre les colonnes.
      const nom = String(l?.designation || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!nom) return '';
      const q = l.quantite != null && Number(l.quantite) > 0 ? Number(l.quantite) : null;
      return q ? `${nom} (${q}${l.unite ? ' ' + l.unite : ''})` : nom;
    })
    .filter(Boolean)
    .join(', ')
    .slice(0, 300);
}

/** Libellés des photos d'une facture prises avant et après les travaux. */
export const PHOTO_AVANT = 'Photo avant';
export const PHOTO_APRES = 'Photo après';
const estUnLibelleDePhoto = (libelle) =>
  libelle === LIBELLE_PHOTO || libelle === PHOTO_AVANT || libelle === PHOTO_APRES;

/** Libellé de l'offre que le client a signée, joint au même endroit. */
const LIBELLE_SIGNEE = 'Offre signée par le client';

/** Ce qu'est une pièce de l'offre ou de la facture, pour la liste unique de la fiche. */
function sorteDe(p, genre) {
  // Sur une facture, le serveur imprime toute pièce sans fournisseur, référence
  // ni montant (photos des travaux) ; sur une offre, seules les « Photo ».
  const photo =
    genre === 'FACTURE'
      ? !String(p.fournisseur || '').trim() &&
        !String(p.reference || '').trim() &&
        (p.montant === null || p.montant === undefined || p.montant === '')
      : estPhotoPourLeClient(p);
  if (photo) return { icone: '🖼', texte: 'Photo pour le client' };
  if (String(p.libelle || '').startsWith(LIBELLE_SIGNEE)) {
    return { icone: '✍', texte: LIBELLE_SIGNEE };
  }
  return {
    icone: '🧾',
    texte:
      genre === 'FACTURE' ? 'Matériel acheté (pour toi)' : 'Facture fournisseur (coût du chantier)',
  };
}

/** Dépose une pièce saisie (fichier facultatif) sur un devis ou une facture enregistré. */
async function depose(genre, documentId, piece) {
  const reduit = piece.fichier ? await reduitLImage(piece.fichier, COTE_MAX) : null;
  // Une facture fournisseur porte toujours un fournisseur : sans lui, le
  // serveur la prendrait pour une photo et l'imprimerait chez le client.
  const fournisseur =
    piece.fournisseur.trim() || (estUnLibelleDePhoto(piece.libelle) ? '' : 'Fournisseur');
  // Les articles achetés tiennent lieu de libellé : la liste les montre.
  const libelle = String(piece.articles || '').trim() || piece.libelle || fournisseur;
  await piecesApi.depose(genre, documentId, reduit, libelle, {
    fournisseur,
    reference: piece.ref.trim(),
    montant: piece.montant === '' ? '' : Number(piece.montant),
    categorie: piece.cat,
    // La TVA imprimée sur la facture, lue au scan : elle se récupère telle quelle.
    montantTva: piece.montantTva ?? null,
    // La date de la facture du fournisseur (AAAA-MM-JJ) ; sans elle, le jour du dépôt.
    date: /^\d{4}-\d{2}-\d{2}$/.test(piece.date || '') ? piece.date : '',
  });
}

/**
 * Factures fournisseur et photos jointes à un devis, présentées comme la
 * maquette V288.
 *
 * Elles ne sont pas de simples photos : elles portent un fournisseur, une
 * référence et un montant. C'est le coût du chantier tel que l'artisan le
 * connaît, en face du prix qu'il annonce — sans elles, la marge ne se vérifie
 * plus après coup.
 *
 * Deux présentations :
 * - `saisie` (formulaire du devis) : bouton « Scanner la facture », fournisseur,
 *   référence, montant, catégorie. Sur un devis pas encore enregistré, les
 *   pièces restent en brouillon (`brouillons`) et partent au serveur dès que le
 *   devis existe (voir `lib/pieces-attente.js`) ;
 * - `fiche` (détail du devis) : une seule liste et un seul bouton « Joindre » ;
 *   l'artisan dit ensuite ce qu'est le fichier (photo pour le client, offre
 *   signée, facture fournisseur).
 *
 * Le fichier est facultatif : on note le fournisseur et le montant de tête, la
 * photo du bon suivra ou ne suivra pas. Avec le Pack IA, la photo scannée est
 * lue et pré-remplit fournisseur, référence et montant.
 *
 * @param {object} props propriétés du composant
 * @param {number|string} props.documentId devis concerné (identifiant serveur, ou provisoire)
 * @param {string} [props.numero] numéro du devis, clé des pièces en attente
 * @param {string[]} [props.categories] familles de dépense proposées
 * @param {'saisie'|'fiche'} [props.variante] présentation
 * @param {'DEVIS'|'FACTURE'} [props.genre] pièce à laquelle s'attachent les fichiers
 * @param {number} [props.montantHt] montant HT de la facture, pour la marge (fiche d'une facture)
 * @param {object[]} [props.brouillons] pièces d'un devis pas encore enregistré
 * @param {(pieces: object[]) => void} [props.onBrouillons] met à jour ces pièces
 * @returns {import('react').ReactElement} la liste et son formulaire
 */
export function FacturesJointes({
  documentId,
  numero,
  categories,
  variante = 'saisie',
  genre = 'DEVIS',
  montantHt,
  brouillons,
  onBrouillons,
  magasin = false,
}) {
  const [pieces, setPieces] = useState([]);
  // Le matériel acheté est une dépense du chantier : le patron et la
  // secrétaire, qui la saisit, le voient ; le technicien voit les photos, pas
  // ce que le chantier a coûté (le serveur ne lui rend pas ces pièces). La
  // marge, elle, reste au patron et à qui il a ouvert le bénéfice.
  const direction = suitLesDepenses();
  const voitLaMarge = voitLeBenefice();
  // Comme la maquette, la première catégorie est retenue d'office.
  const neuf = () => ({ ...VIDE, cat: (categories || [])[0] || '' });
  const [brouillon, setBrouillon] = useState(neuf);
  const [fichier, setFichier] = useState(null);
  const [apercu, setApercu] = useState('');
  // La photo ouverte en grand, dans l'application.
  const [enGrand, setEnGrand] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [formulaire, setFormulaire] = useState(variante === 'saisie');
  // Fiche : fichiers choisis par « Joindre », en attente de leur sorte.
  const [aClasser, setAClasser] = useState([]);
  const [pourFacture, setPourFacture] = useState(null);
  const enregistre = estEnregistre(documentId);
  const enBrouillon = !enregistre && typeof onBrouillons === 'function';

  const rafraichit = useCallback(async () => {
    if (!enregistre) {
      return;
    }
    try {
      setPieces(await piecesApi.liste(genre, documentId));
    } catch (e) {
      setErreur(e?.message || 'Les factures jointes n’ont pas pu être lues.');
    }
  }, [documentId, enregistre, genre]);

  // Le devis vient d'être créé : les factures saisies avant partent maintenant.
  useEffect(() => {
    if (!enregistre) {
      return;
    }
    const attente = prendEnAttente(numero, genre);
    if (attente.length === 0) {
      rafraichit();
      return;
    }
    (async () => {
      try {
        for (const p of attente) {
          await depose(genre, documentId, p);
        }
      } catch (e) {
        setErreur(e?.message || 'Une facture jointe n’a pas pu être envoyée.');
      }
      rafraichit();
    })();
  }, [enregistre, documentId, numero, rafraichit, genre]);

  const rien =
    !fichier && !brouillon.fournisseur.trim() && !brouillon.ref.trim() && !brouillon.montant;

  const remetAZero = () => {
    setBrouillon(neuf());
    setFichier(null);
    setApercu('');
  };

  const ajoute = async () => {
    setErreur('');
    if (enBrouillon) {
      onBrouillons([
        ...(brouillons || []),
        { ...brouillon, fichier, apercu, id: 'b' + Date.now() + Math.random() },
      ]);
      remetAZero();
      return;
    }
    if (!enregistre) {
      return;
    }
    setEnvoi(true);
    try {
      await depose(genre, documentId, { ...brouillon, fichier });
      remetAZero();
      if (variante === 'fiche') setFormulaire(false);
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'La facture n’a pas pu être jointe.');
    } finally {
      setEnvoi(false);
    }
  };

  // Une ou plusieurs photos d'un coup, avant ou après l'enregistrement : elles
  // figurent dans le PDF de l'offre (annexe photos), à l'écran et chez le client.
  const ajoutePhotos = async (fichiers, libelle = LIBELLE_PHOTO) => {
    const liste = Array.from(fichiers || []).filter(Boolean);
    if (liste.length === 0) {
      return;
    }
    setErreur('');
    if (enBrouillon) {
      onBrouillons([
        ...(brouillons || []),
        ...liste.map((f) => ({
          ...VIDE,
          libelle: LIBELLE_PHOTO,
          fichier: f,
          apercu: typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(f) : '',
          id: 'b' + Date.now() + Math.random(),
        })),
      ]);
      return;
    }
    if (!enregistre) {
      return;
    }
    setEnvoi(true);
    try {
      for (const f of liste) {
        await depose(genre, documentId, { ...VIDE, fichier: f, libelle });
      }
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'La photo n’a pas pu être jointe.');
      await rafraichit();
    } finally {
      setEnvoi(false);
    }
  };

  // L'offre signée, telle qu'elle est revenue : scannée, en photo ou en PDF.
  const joinsSignees = async (fichiers) => {
    setErreur('');
    setEnvoi(true);
    try {
      for (const f of fichiers) {
        const pret = String(f.type || '').startsWith('image/') ? await reduitLImage(f) : f;
        await piecesApi.depose(genre, documentId, pret, LIBELLE_SIGNEE);
      }
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || tr('Le document n’a pas pu être joint.'));
      await rafraichit();
    } finally {
      setEnvoi(false);
    }
  };

  const classe = (sorte) => {
    const fichiers = aClasser;
    setAClasser([]);
    if (sorte === 'photo') ajoutePhotos(fichiers);
    else if (sorte === 'signee') joinsSignees(fichiers);
    else {
      remetAZero();
      setFormulaire(true);
      setPourFacture(fichiers[0]);
    }
  };

  const retire = async (piece) => {
    if (piece.brouillon) {
      onBrouillons((brouillons || []).filter((x) => x.id !== piece.id));
      return;
    }
    if (
      !window.confirm(
        genre === 'FACTURE'
          ? tr('Supprimer cette pièce de la facture ?')
          : tr('Retirer cette pièce de l’offre ?'),
      )
    ) {
      return;
    }
    setErreur('');
    try {
      await piecesApi.retire(piece.id);
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  // Ce qui s'affiche : les pièces du serveur, ou celles qui attendent le devis.
  const liste = enregistre
    ? pieces.map((p) => ({
        id: p.id,
        piece: p,
        sorte: sorteDe(p, genre),
        nom:
          p.fournisseur ||
          (p.libelle === PHOTO_AVANT
            ? tr('📷 Avant les travaux')
            : p.libelle === PHOTO_APRES
              ? tr('📷 Après les travaux')
              : p.libelle || 'Photo'),
        // Pour le matériel acheté, les articles d'abord : on voit ce qui a été pris.
        detail: [
          p.libelle && p.libelle !== p.fournisseur && p.libelle !== 'Photo' && p.fournisseur
            ? p.libelle
            : '',
          p.categorie,
          p.reference,
          // La date à la suisse (26.10.2026), pas « 2026-10-26 ».
          /^\d{4}-\d{2}-\d{2}$/.test(p.date || '') ? isoVersDate(p.date) : p.date,
        ]
          .filter(Boolean)
          .join(' · '),
        montant: +p.montant || 0,
        montantTva: p.montantTva ?? null,
        image:
          p.fichier && String(p.typeMedia || 'image/').startsWith('image/')
            ? adresseApi(p.fichier)
            : '',
      }))
    : (enBrouillon ? brouillons || [] : enAttente(numero, genre)).map((p) => ({
        id: p.id,
        brouillon: enBrouillon,
        nom: p.fournisseur || (p.libelle === LIBELLE_PHOTO ? 'Photo' : 'Facture'),
        detail: [p.cat, p.ref].filter(Boolean).join(' · '),
        montant: +p.montant || 0,
        montantTva: p.montantTva ?? null,
        image: p.apercu || '',
      }));
  const total = liste.reduce((s, p) => s + p.montant, 0);
  // Le même hors-taxe que le chantier et l'accueil (lib/costs.js) : montant
  // TTC moins la TVA lue sur la pièce, à défaut celle du taux normal.
  const totalHt = auCentime(liste.reduce((s, p) => s + p.montant - tvaFactureFournisseur(p), 0));
  const fiche = variante === 'fiche';
  const styleChoixFacture = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    border: '1.5px dashed',
    borderRadius: 10,
    padding: '12px 8px',
    fontSize: 11.5,
    fontWeight: 800,
    textAlign: 'center',
    cursor: envoi ? 'wait' : 'pointer',
    boxSizing: 'border-box',
  };

  const ligne = (p) => (
    <div
      key={p.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        background: C.navyXL,
        borderRadius: fiche ? 8 : 9,
        padding: fiche ? '7px 9px' : '8px 10px',
        marginBottom: fiche ? 5 : 6,
      }}
    >
      {p.image ? (
        // Un lien vers un nouvel onglet ne s'ouvrait pas dans l'application
        // installée sur l'iPhone : la photo s'ouvre en grand ici même.
        <button
          type="button"
          aria-label={tr('Voir la photo en grand')}
          onClick={() => setEnGrand(p)}
          style={{
            border: 'none',
            padding: 0,
            background: 'none',
            cursor: 'zoom-in',
            lineHeight: 0,
          }}
        >
          <img src={p.image} alt={p.nom} style={vignette(fiche ? 38 : 36)} />
        </button>
      ) : (
        fiche &&
        p.sorte && (
          <button
            aria-label={tr('Voir la pièce')}
            onClick={() => p.piece?.fichier && ouvrePiece(p.piece)}
            style={{
              ...vignette(38),
              background: C.white,
              fontSize: 16,
              cursor: p.piece?.fichier ? 'pointer' : 'default',
              padding: 0,
            }}
          >
            {String(p.piece?.typeMedia || '').includes('pdf') ? '📄' : p.sorte.icone}
          </button>
        )
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: fiche ? 9.5 : 10, fontWeight: 700, color: C.ink }}>
          {fiche && p.sorte?.texte === LIBELLE_SIGNEE ? tr(LIBELLE_SIGNEE) : p.nom}
        </div>
        <div style={{ fontSize: 8, color: C.muted }}>
          {fiche && p.sorte ? [tr(p.sorte.texte), p.detail].filter(Boolean).join(' · ') : p.detail}
        </div>
        {/* Une facture de fournisseur ou de sous-traitant : le jour où elle a été
            payée, saisi ici tant que la banque n'est pas reliée. */}
        {enregistre && p.piece && p.montant > 0 && (
          <PayeLe
            payeLe={isoVersDate(p.piece.payeLe)}
            payePar={String(p.piece.payePar || '').toLowerCase()}
            onChange={async (v) => {
              try {
                const a = await piecesApi.paiement(p.id, v.payeLe, v.payePar);
                setPieces((l) => l.map((x) => (x.id === p.id ? { ...x, ...a } : x)));
              } catch (e) {
                window.alert(e?.message || tr('Le paiement n’a pas pu être noté.'));
              }
            }}
          />
        )}
      </div>
      {p.montant > 0 && (
        <span style={{ fontSize: fiche ? 10 : 10.5, fontWeight: 800, color: C.purple }}>
          {DEVISE} {fmt(p.montant)}
        </span>
      )}
      {(enregistre || p.brouillon) && (
        <button
          aria-label="Retirer la pièce"
          onClick={() => retire(p)}
          // Une pièce jointe par erreur se supprime d'un geste : le petit « ✕ »
          // gris de la fiche passait inaperçu.
          style={
            fiche
              ? {
                  background: C.redL,
                  border: 'none',
                  borderRadius: 7,
                  color: C.red,
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: '6px 9px',
                }
              : {
                  background: C.redL,
                  border: 'none',
                  borderRadius: 6,
                  color: C.red,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: 11,
                }
          }
        >
          🗑
        </button>
      )}
      {enGrand?.id === p.id && p.image && (
        <PhotoPleinEcran src={p.image} legende={p.nom} onFerme={() => setEnGrand(null)} />
      )}
    </div>
  );

  const champs = (
    <div>
      {fichier ? (
        <div style={{ position: 'relative', marginBottom: 10 }}>
          {apercu.startsWith('data:image') ? (
            <img
              src={apercu}
              alt={'Facture scannée ' + (fichier.name || '')}
              style={{
                width: '100%',
                maxHeight: 150,
                objectFit: 'contain',
                borderRadius: 8,
                border: '1px solid ' + C.border,
              }}
            />
          ) : (
            <div
              style={{
                background: C.navyXL,
                borderRadius: 8,
                padding: '12px 36px 12px 12px',
                fontSize: 10,
                fontWeight: 700,
                color: C.ink,
                wordBreak: 'break-all',
              }}
            >
              📎 {fichier.name || 'Pièce jointe'}
            </div>
          )}
          <button
            aria-label="Retirer la photo"
            onClick={() => {
              setFichier(null);
              setApercu('');
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              background: C.red,
              border: 'none',
              borderRadius: 99,
              color: '#fff',
              width: 24,
              height: 24,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      ) : null}
      {/* Un seul bouton : sur le téléphone, il propose la photo, la
          photothèque ou un fichier (PDF). Le fichier joint, le bouton
          s'efface mais la lecture reste à l'écran : « Lecture… », puis
          « ✓ Facture lue » ou la raison d'un échec — sans quoi les cases
          restaient vides sans un mot. */}
      <ScanFacture
        sansBouton={!!fichier}
        label={tr('📎 Joindre une facture fournisseur (photo ou PDF)')}
        color={C.purple}
        style={{ marginBottom: 10 }}
        boutonStyle={{ borderRadius: 9, padding: 14, fontSize: 10.5 }}
        aLire={pourFacture}
        onPhoto={(a, f) => {
          setFichier(f || null);
          setApercu(typeof a === 'string' ? a : '');
        }}
        onLecture={(lu) => {
          const f = champsFacture(lu);
          setBrouillon((p) => ({
            ...p,
            fournisseur: f.fournisseur ?? p.fournisseur,
            ref: f.ref ?? p.ref,
            montant: f.montant != null ? String(f.montant) : p.montant,
            montantTva: f.montantTva ?? null,
            articles: articlesLus(lu) || p.articles,
            date: f.date || p.date,
          }));
        }}
      />
      {/* Sur la nouvelle offre, les cases n'apparaissent qu'avec la facture
          jointe, lue toute seule : à ce stade rien n'est encore acheté, et
          « Saisir à la main » n'y servait à rien (demande du patron). */}
      {variante !== 'saisie' || fichier ? (
        <>
          {genre === 'FACTURE' && (
            <textarea
              aria-label={tr('Articles achetés')}
              value={brouillon.articles}
              onChange={(e) =>
                setBrouillon((p) => ({ ...p, articles: e.target.value.slice(0, 300) }))
              }
              placeholder={tr('Articles achetés (lus tout seuls sur la facture)')}
              // Tous les articles lus se voient d'un coup : sur deux lignes, la
              // liste était coupée au milieu du deuxième article.
              rows={Math.min(
                7,
                Math.max(2, Math.ceil(String(brouillon.articles || '').length / 38)),
              )}
              style={{ ..._inp, marginBottom: 7, resize: 'vertical', fontFamily: 'inherit' }}
            />
          )}
          <input
            aria-label="Fournisseur"
            value={brouillon.fournisseur}
            onChange={(e) => setBrouillon((p) => ({ ...p, fournisseur: e.target.value }))}
            placeholder="Fournisseur / sous-traitant"
            style={{ ..._inp, marginBottom: 7 }}
          />
          {/* La date de la facture du fournisseur, lue avec elle : elle range la
            dépense dans le bon mois et le bon trimestre de TVA. */}
          <label
            style={{
              display: 'block',
              fontSize: 9,
              fontWeight: 700,
              color: C.muted,
              marginBottom: 7,
            }}
          >
            {tr("Date d'achat")}
            <input
              type="date"
              aria-label={tr("Date d'achat")}
              value={brouillon.date || ''}
              onChange={(e) => setBrouillon((p) => ({ ...p, date: e.target.value }))}
              style={{ ..._inp, marginTop: 3 }}
            />
          </label>
          <div style={{ display: 'flex', gap: 7, marginBottom: 7 }}>
            <input
              aria-label="Référence"
              value={brouillon.ref}
              onChange={(e) => setBrouillon((p) => ({ ...p, ref: e.target.value }))}
              placeholder="Référence"
              style={{ ..._inp }}
            />
            <input
              aria-label={'Montant ' + DEVISE}
              inputMode="decimal"
              value={brouillon.montant}
              onChange={(e) =>
                // Un montant corrigé à la main : la TVA lue ne lui correspond plus.
                setBrouillon((p) => ({
                  ...p,
                  montant: e.target.value.replace(',', '.'),
                  montantTva: null,
                }))
              }
              placeholder={trf('Total TTC {0}', DEVISE)}
              style={{ ..._inp }}
            />
          </div>
          {/* Le patron ne savait pas s'il fallait le prix avec ou sans TVA : c'est
            le total à payer, TVA comprise, que la marge et la TVA récupérable
            décomposent ensuite. */}
          <div style={{ fontSize: 9.5, color: C.muted, margin: '-2px 0 9px' }}>
            {tr('Montant : le total de la facture, TVA comprise. La TVA se récupère toute seule.')}
          </div>
          {(categories || []).length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 5 }}>
                Catégorie
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                {categories.map((c) => (
                  <div
                    key={c}
                    onClick={() => setBrouillon((p) => ({ ...p, cat: p.cat === c ? '' : c }))}
                    style={{
                      padding: '5px 9px',
                      borderRadius: 8,
                      fontSize: 9,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: '1px solid ' + (brouillon.cat === c ? C.purple : C.border),
                      background: brouillon.cat === c ? C.purple : C.white,
                      color: brouillon.cat === c ? '#fff' : C.grey,
                    }}
                  >
                    {c}
                  </div>
                ))}
              </div>
            </>
          )}
          <Btn
            label={
              envoi
                ? 'Envoi…'
                : genre === 'FACTURE'
                  ? tr('✓ Ajouter ce matériel à la facture')
                  : tr('✓ Ajouter cette facture à l’offre')
            }
            sm
            color={C.purple}
            full
            disabled={rien || envoi || (!enregistre && !enBrouillon)}
            onClick={ajoute}
          />
        </>
      ) : null}
    </div>
  );

  const message = erreur && (
    <div
      role="alert"
      style={{
        marginTop: 7,
        fontSize: 9.5,
        color: C.red,
        background: C.red + '15',
        borderRadius: 7,
        padding: '6px 9px',
        lineHeight: 1.5,
      }}
    >
      {erreur}
    </div>
  );

  const boutonPhotos = (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        border: '1px dashed ' + C.purple,
        borderRadius: 9,
        padding: '14px',
        fontSize: 10.5,
        fontWeight: 700,
        color: C.purple,
        cursor: envoi ? 'wait' : 'pointer',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {envoi ? 'Envoi…' : '📷 Ajouter des photos'}
      <input
        type="file"
        accept="image/*"
        multiple
        aria-label="Photo à joindre"
        disabled={envoi}
        style={{ display: 'none' }}
        onChange={(e) => {
          const choisis = Array.from(e.target.files || []);
          e.target.value = '';
          ajoutePhotos(choisis);
        }}
      />
    </label>
  );

  if (fiche) {
    const tousImages =
      aClasser.length > 0 && aClasser.every((f) => String(f.type || '').startsWith('image/'));
    const choix = (sorte, icone, titre, aide, actif) => (
      <button
        key={sorte}
        disabled={!actif}
        onClick={() => classe(sorte)}
        style={{
          border: '1.5px solid ' + C.border,
          background: C.white,
          borderRadius: 13,
          padding: 14,
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          color: C.ink,
          cursor: actif ? 'pointer' : 'not-allowed',
          opacity: actif ? 1 : 0.45,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 800 }}>
          {icone} {tr(titre)}
        </span>
        <span style={{ fontSize: 11.5, color: C.muted }}>{tr(aide)}</span>
      </button>
    );
    return (
      <div>
        {liste.map(ligne)}
        {!enregistre ? (
          <div style={{ fontSize: 8.5, color: C.muted, textAlign: 'center', padding: 6 }}>
            Enregistrement du devis en cours…
          </div>
        ) : formulaire ? (
          <div style={{ marginTop: 8 }}>
            <div style={titreSection}>
              {genre === 'FACTURE' ? tr('🧾 MATÉRIEL ACHETÉ') : tr('🧾 FACTURE FOURNISSEUR')}
            </div>
            {champs}
            <div
              role="button"
              onClick={() => {
                setFormulaire(false);
                remetAZero();
              }}
              style={{
                fontSize: 10,
                color: C.muted,
                textAlign: 'center',
                marginTop: 8,
                cursor: 'pointer',
              }}
            >
              {tr('Annuler')}
            </div>
          </div>
        ) : genre === 'FACTURE' ? (
          // Sur une facture, deux gestes : la photo des travaux pour le client,
          // ou la dépense de matériel — photo, scan ou pièce jointe, lue toute
          // seule (fournisseur, articles, prix). Le matériel ne regarde que le
          // patron.
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Avant et après les travaux, même la facture envoyée ou payée :
                les photos restent avec la facture et dans le classeur. */}
            {/* Au magasin, pas de travaux : un seul bouton de photos. */}
            {(magasin
              ? [[PHOTO_APRES, tr('📷 Photos'), tr('Photos de la marchandise')]]
              : [
                  [PHOTO_AVANT, tr('📷 Photos avant'), tr('Photos avant les travaux')],
                  [PHOTO_APRES, tr('📷 Photos après'), tr('Photos après les travaux')],
                ]
            ).map(([libelle, texte, aria]) => (
              <label
                key={libelle}
                style={{ ...styleChoixFacture, borderColor: C.teal, color: C.teal }}
              >
                {envoi ? tr('⏳ Envoi…') : texte}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  aria-label={aria}
                  disabled={envoi}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const choisis = Array.from(e.target.files || []);
                    e.target.value = '';
                    ajoutePhotos(choisis, libelle);
                  }}
                />
              </label>
            ))}
            {direction && (
              <label
                style={{
                  ...styleChoixFacture,
                  borderColor: C.purple,
                  color: C.purple,
                  gridColumn: '1 / -1',
                }}
              >
                {tr('🧾 Dépense de matériel')}
                <span style={{ fontSize: 9, fontWeight: 600, color: C.muted }}>
                  {tr('photo, scan ou pièce jointe')}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  aria-label={tr('Dépense de matériel')}
                  disabled={envoi}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const choisi = (e.target.files || [])[0];
                    e.target.value = '';
                    if (!choisi) return;
                    remetAZero();
                    setFormulaire(true);
                    setPourFacture(choisi);
                  }}
                />
              </label>
            )}
          </div>
        ) : (
          // Un seul bouton : sur le téléphone, il propose de prendre une
          // photo (scanner une feuille), la photothèque ou un fichier (PDF).
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.5px dashed ' + C.teal,
              borderRadius: 10,
              padding: 14,
              fontSize: 11.5,
              fontWeight: 800,
              color: C.teal,
              cursor: envoi ? 'wait' : 'pointer',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {envoi ? tr('⏳ Envoi…') : tr('📎 Joindre (PDF, photo ou scan)')}
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              aria-label={tr('Fichier à joindre')}
              disabled={envoi}
              style={{ display: 'none' }}
              onChange={(e) => {
                const choisis = Array.from(e.target.files || []);
                e.target.value = '';
                setAClasser(choisis);
              }}
            />
          </label>
        )}
        {aClasser.length > 0 && (
          <div
            role="dialog"
            aria-label={tr('C’est quoi, ce document ?')}
            onClick={() => setAClasser([])}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,.6)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: C.white,
                borderRadius: '22px 22px 0 0',
                padding: '18px 14px calc(24px + env(safe-area-inset-bottom, 0px))',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                width: '100%',
                maxWidth: 520,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 800, color: C.ink }}>
                {tr('C’est quoi, ce document ?')}
              </div>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  color: C.muted,
                  wordBreak: 'break-all',
                }}
              >
                {aClasser.length === 1 ? aClasser[0].name : trf('{0} fichiers', aClasser.length)}
              </div>
              {choix(
                'photo',
                '🖼',
                'Photo pour le client',
                !tousImages
                  ? 'Seulement pour une photo (pas un PDF).'
                  : genre === 'FACTURE'
                    ? 'Elle paraît dans le PDF de la facture envoyé au client.'
                    : 'Elle paraît dans le PDF de l’offre envoyé au client.',
                tousImages,
              )}
              {genre !== 'FACTURE' &&
                choix(
                  'signee',
                  '✍',
                  'Offre signée par le client',
                  'La feuille signée, scannée ou en PDF. Gardée comme preuve.',
                  true,
                )}
              {direction &&
                choix(
                  'facture',
                  '🧾',
                  genre === 'FACTURE'
                    ? 'Matériel acheté (facture fournisseur)'
                    : 'Facture fournisseur',
                  aClasser.length !== 1
                    ? 'Une facture à la fois : choisis un seul fichier.'
                    : genre === 'FACTURE'
                      ? 'Pour toi : le client ne la voit pas. Montant et fournisseur lus tout seuls.'
                      : 'Pour toi : coût du chantier. Montant et fournisseur lus tout seuls.',
                  aClasser.length === 1,
                )}
              <div
                role="button"
                onClick={() => setAClasser([])}
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  color: C.muted,
                  marginTop: 2,
                  cursor: 'pointer',
                }}
              >
                {tr('Annuler')}
              </div>
            </div>
          </div>
        )}
        {message}
        {total > 0 && genre !== 'FACTURE' && <Total total={total} />}
        {voitLaMarge && genre === 'FACTURE' && total > 0 && Number(montantHt) > 0 && (
          // Ce que le matériel coûte et ce qu'il reste au patron : la marge de
          // la facture. Le client ne voit jamais ce bloc. Le matériel compte
          // hors TVA, comme la facture : sa TVA se récupère. Le bloc retranchait
          // le TTC d'un hors-taxe (5'000 − 5'405 = « il te reste −405 »).
          <div
            style={{
              marginTop: 8,
              background: C.navyXL,
              borderRadius: 9,
              padding: '9px 11px',
              fontSize: 11,
              color: C.ink,
            }}
          >
            <div style={{ fontSize: 9.5, fontWeight: 800, color: C.grey, marginBottom: 4 }}>
              {tr('💰 POUR TOI (LE CLIENT NE LE VOIT PAS)')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{tr('Facture HT')}</span>
              <b>{fmt(Number(montantHt))}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{tr('Matériel acheté (hors TVA)')}</span>
              <b>− {fmt(totalHt)}</b>
            </div>
            <div style={{ fontSize: 9, color: C.grey }}>
              {trf('TVA à récupérer sur ce matériel : {0}', DEVISE + ' ' + fmt(total - totalHt))}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 800,
                color: Number(montantHt) - totalHt >= 0 ? C.green : C.red,
                borderTop: '1px solid ' + C.border,
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <span>{tr('Il te reste')}</span>
              <span>
                {DEVISE} {fmt(Number(montantHt) - totalHt)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {(enregistre || enBrouillon) && (
        <div style={{ marginBottom: 12 }}>
          <div style={titreSection}>{tr('POUR LE CLIENT')}</div>
          {boutonPhotos}
          <div style={{ fontSize: 10, color: C.muted, marginTop: 5 }}>
            {tr('Elles figurent dans le PDF de l’offre envoyé au client.')}
          </div>
        </div>
      )}
      {direction && <div style={titreSection}>{tr('POUR TOI — COÛT DU CHANTIER')}</div>}
      {direction && champs}
      {message}
      {liste.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          {liste.map(ligne)}
          {direction && total > 0 && <Total total={total} />}
        </div>
      ) : (
        <div style={{ fontSize: 8.5, color: C.muted, textAlign: 'center', marginTop: 8 }}>
          Aucune photo ni facture pour l&apos;instant.
        </div>
      )}
    </div>
  );
}

const titreSection = {
  fontSize: 9.5,
  fontWeight: 800,
  color: C.grey,
  letterSpacing: 0.4,
  marginBottom: 6,
};

function vignette(cote) {
  return {
    width: cote,
    height: cote,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid ' + C.border,
    flexShrink: 0,
    display: 'block',
  };
}

function Total({ total }) {
  return (
    <div style={{ textAlign: 'right', fontSize: 9, fontWeight: 700, color: C.grey, marginTop: 2 }}>
      Total factures jointes : {DEVISE} {fmt(total)}
    </div>
  );
}
